// src/components/BulkOperations/BulkActionStatusModal.js
import React, { useMemo, useState, useEffect } from "react";
import BulkProgressBar from "./BulkProgressBar";
import BulkResultsList from "./BulkResultsList";
import logger from "../../utils/logger";
import "./BulkActionStatusModal.css";

/**
 * BulkActionStatusModal - Main modal for displaying bulk operation status
 *
 * Shows progress bars, timing statistics, and per-device results for bulk operations
 * Supports both lockdown and release actions
 */
const BulkActionStatusModal = ({
  isOpen,
  onClose,
  actionResults,
  actionStatuses,
  actionTimestamps = {},
  deviceStates,
  actionType,
  installationName,
  deviceLockStates = {},
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [deviceElapsedTime, setDeviceElapsedTime] = useState(0);
  const [isDeviceTimerRunning, setIsDeviceTimerRunning] = useState(true);
  const [deviceTimers, setDeviceTimers] = useState({});

  // Initialize device timers when modal opens
  useEffect(() => {
    if (!isOpen || !actionResults) {
      setDeviceTimers({});
      return;
    }

    const now = Date.now();
    const initialTimers = {};
    actionResults.forEach((result) => {
      // Initially use current time - will be updated when timestamps arrive
      initialTimers[result.deviceId] = {
        cloudElapsed: 0,
        deviceElapsed: 0,
        cloudStartTime: now,
        deviceStartTime: now,
        cloudStopped: false,
        deviceStopped: false,
      };
    });
    setDeviceTimers(initialTimers);
  }, [isOpen, actionResults]);

  // Update timer start times when backend timestamps arrive
  useEffect(() => {
    if (
      !isOpen ||
      !actionResults ||
      Object.keys(actionTimestamps).length === 0
    ) {
      return;
    }

    setDeviceTimers((prevTimers) => {
      let hasChanges = false;
      const updatedTimers = { ...prevTimers };

      actionResults.forEach((result) => {
        const timer = updatedTimers[result.deviceId];
        if (!timer) return;

        const timestamps = actionTimestamps[result.actionId];
        if (!timestamps?.createdAt) return;

        const actionCreatedTime = new Date(timestamps.createdAt).getTime();

        // Only update if we haven't already set this timestamp
        if (timer.cloudStartTime !== actionCreatedTime) {
          const cloudUpdatedTime = timestamps.cloudUpdatedAt
            ? new Date(timestamps.cloudUpdatedAt).getTime()
            : null;
          const expectedReactionMs = cloudUpdatedTime
            ? cloudUpdatedTime - actionCreatedTime
            : null;

          logger.log(
            `[BulkActionStatusModal] 🕐 Updating timer start time for action ${result.actionId}:`,
            {
              deviceId: result.deviceId,
              oldStartTime: new Date(timer.cloudStartTime).toISOString(),
              newStartTime: new Date(actionCreatedTime).toISOString(),
              createdAt: timestamps.createdAt,
              cloudCreatedAt: timestamps.cloudCreatedAt,
              cloudUpdatedAt: timestamps.cloudUpdatedAt,
              expectedReactionMs,
              allTimestamps: timestamps,
            }
          );

          timer.cloudStartTime = actionCreatedTime;
          timer.deviceStartTime = actionCreatedTime;
          hasChanges = true;
        }
      });

      return hasChanges ? updatedTimers : prevTimers;
    });
  }, [isOpen, actionResults, actionTimestamps]);

  // Update per-device timers every 100ms
  useEffect(() => {
    if (!isOpen || !actionResults || actionResults.length === 0) return;

    const interval = setInterval(() => {
      setDeviceTimers((prevTimers) => {
        const now = Date.now();
        let hasChanges = false;
        const updatedTimers = { ...prevTimers };

        actionResults.forEach((result) => {
          const timer = updatedTimers[result.deviceId];
          if (!timer) return;

          const status = actionStatuses[result.actionId];
          const deviceLockState = deviceLockStates[result.deviceId];
          const expectedState =
            actionType === "LOCK_DOWN" ? "Lockdown On" : "Lockdown Off";

          // Update cloud timer until RESOLVED
          if (status !== "RESOLVED" && !timer.cloudStopped) {
            timer.cloudElapsed = now - timer.cloudStartTime;
            hasChanges = true;
          } else if (status === "RESOLVED" && !timer.cloudStopped) {
            timer.cloudElapsed = now - timer.cloudStartTime;
            timer.cloudStopped = true;
            hasChanges = true;

            // Log when cloud timer stops to debug 0.000 issue
            logger.log(
              `[BulkActionStatusModal] ⏱️ Cloud timer stopped for device ${result.deviceId}:`,
              {
                actionId: result.actionId,
                cloudElapsed: timer.cloudElapsed,
                cloudStartTime: new Date(timer.cloudStartTime).toISOString(),
                now: new Date(now).toISOString(),
                diff: now - timer.cloudStartTime,
              }
            );
          }

          // Update device timer until state matches
          if (deviceLockState !== expectedState && !timer.deviceStopped) {
            timer.deviceElapsed = now - timer.deviceStartTime;
            hasChanges = true;
          } else if (
            deviceLockState === expectedState &&
            !timer.deviceStopped
          ) {
            timer.deviceElapsed = now - timer.deviceStartTime;
            timer.deviceStopped = true;
            hasChanges = true;
          }
        });

        // Only return new object if something actually changed
        return hasChanges ? updatedTimers : prevTimers;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, actionResults, actionStatuses, deviceLockStates, actionType]);

  // Global cloud response timer
  useEffect(() => {
    if (!isOpen) {
      setElapsedTime(0);
      setIsTimerRunning(true);
      return;
    }

    if (!isTimerRunning) return;

    const interval = setInterval(() => {
      setElapsedTime((prev) => prev + 100);
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, isTimerRunning]);

  // Global device reaction timer
  useEffect(() => {
    if (!isOpen) {
      setDeviceElapsedTime(0);
      setIsDeviceTimerRunning(true);
      return;
    }

    if (!isDeviceTimerRunning) return;

    const interval = setInterval(() => {
      setDeviceElapsedTime((prev) => prev + 100);
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, isDeviceTimerRunning]);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    if (!actionResults || actionResults.length === 0) {
      return { resolved: 0, pending: 0, rejected: 0 };
    }

    const counts = { resolved: 0, pending: 0, rejected: 0 };
    actionResults.forEach((result) => {
      const status = actionStatuses[result.actionId] || "PENDING";
      if (status === "RESOLVED") counts.resolved++;
      else if (status === "REJECTED") counts.rejected++;
      else counts.pending++;
    });

    return counts;
  }, [actionResults, actionStatuses]);

  // Check if all actions are complete
  const allActionsComplete = useMemo(() => {
    if (!actionResults || actionResults.length === 0) return false;
    const allResolved = actionResults.every(
      (result) => actionStatuses[result.actionId] === "RESOLVED"
    );
    if (allResolved && isTimerRunning) {
      setIsTimerRunning(false);
    }
    return allResolved;
  }, [actionResults, actionStatuses, isTimerRunning]);

  // Check if all devices have reacted
  const allDevicesReacted = useMemo(() => {
    if (!actionResults || actionResults.length === 0) return false;

    const expectedState =
      actionType === "LOCK_DOWN" ? "Lockdown On" : "Lockdown Off";

    const allConfirmed = actionResults.every(
      (result) => deviceLockStates[result.deviceId] === expectedState
    );

    if (allConfirmed && isDeviceTimerRunning) {
      setIsDeviceTimerRunning(false);
    }

    return allConfirmed;
  }, [actionResults, deviceLockStates, actionType, isDeviceTimerRunning]);

  // Calculate cloud response progress percentage
  const progress = useMemo(() => {
    if (!actionResults || actionResults.length === 0) return 0;

    logger.log("[CloudProgress] Calculating cloud response progress...");
    logger.log("[CloudProgress] Action results count:", actionResults.length);
    logger.log("[CloudProgress] Action statuses:", actionStatuses);

    const resolvedCount = actionResults.filter((result) => {
      const status = actionStatuses[result.actionId];
      logger.log(
        `[CloudProgress] Device ${result.deviceId}, ActionID ${result.actionId}, Status: ${status}`
      );
      return status === "RESOLVED";
    }).length;

    const percentage = Math.round((resolvedCount / actionResults.length) * 100);
    logger.log("[CloudProgress] Resolved count:", resolvedCount);
    logger.log("[CloudProgress] Percentage:", percentage);

    return percentage;
  }, [actionResults, actionStatuses]);

  // Calculate device response progress percentage
  const deviceProgress = useMemo(() => {
    if (!actionResults || actionResults.length === 0) return 0;

    const expectedState =
      actionType === "LOCK_DOWN" ? "Lockdown On" : "Lockdown Off";

    logger.log("[DeviceProgress] Calculating...");
    logger.log("[DeviceProgress] Expected state:", expectedState);

    const confirmedCount = actionResults.filter((result) => {
      const deviceState = deviceLockStates[result.deviceId];
      return deviceState === expectedState;
    }).length;

    const percentage = Math.round(
      (confirmedCount / actionResults.length) * 100
    );
    logger.log("[DeviceProgress] Percentage:", percentage);
    return percentage;
  }, [actionResults, deviceLockStates, actionType]);

  const getActionTypeLabel = () => {
    if (actionType === "LOCK_DOWN") return "Bulk Lockdown";
    if (actionType === "RELEASE") return "Bulk Release";
    return "Bulk Action";
  };

  if (!isOpen) return null;

  return (
    <div className="bulk-action-status-overlay">
      <div className="bulk-action-status-modal">
        <div className="bulk-action-status-header">
          <div className="header-title-section">
            <h2>{getActionTypeLabel()} Status</h2>
            <p className="installation-subtitle">{installationName}</p>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="bulk-action-status-content">
          <BulkProgressBar
            progress={progress}
            elapsedTime={elapsedTime}
            label="Cloud Response Resolution"
            statusCounts={statusCounts}
            isComplete={allActionsComplete}
          />

          <BulkProgressBar
            progress={deviceProgress}
            elapsedTime={deviceElapsedTime}
            label="Device Reaction Confirmation"
            statusCounts={{
              resolved:
                actionResults?.filter(
                  (r) =>
                    deviceLockStates[r.deviceId] ===
                    (actionType === "LOCK_DOWN"
                      ? "Lockdown On"
                      : "Lockdown Off")
                ).length || 0,
              pending:
                actionResults?.filter(
                  (r) =>
                    deviceLockStates[r.deviceId] !==
                    (actionType === "LOCK_DOWN"
                      ? "Lockdown On"
                      : "Lockdown Off")
                ).length || 0,
              rejected: 0,
            }}
            isComplete={allDevicesReacted}
            isDeviceProgress={true}
          />

          <BulkResultsList
            actionResults={actionResults}
            actionStatuses={actionStatuses}
            deviceStates={deviceStates}
            deviceLockStates={deviceLockStates}
            actionType={actionType}
            deviceTimers={deviceTimers}
          />

          {actionResults && actionResults.length === 0 && (
            <div className="no-results">
              <p>No action results to display</p>
            </div>
          )}
        </div>

        <div className="bulk-action-status-footer">
          <button className="close-footer-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkActionStatusModal;
